import React from "react";
import {
  Button,
  Card,
  CardActionArea,
  CardContent,
  CardHeader,
} from "@mui/material";
import { MyResponse } from "../../types";
import { ResponseState } from "./State";
import { ResponseProgress } from "./Progress";

export interface Props {
  response: MyResponse;
}

function UserAction({
  response,
  needsResponse,
  submittable,
}: {
  response: MyResponse;
  submittable: boolean;
  needsResponse: boolean;
}) {
  // Campaign has student feedback enabled and user still needs to respond
  if (needsResponse) {
    return (
      <Button
        href={`${response.campaignResultsLink}#respond`}
        variant="contained"
      >
        Respond to Results
      </Button>
    );
  }

  // Campaign is open and user can still respond
  if (submittable) {
    return (
      <Button href={response.tokenizedLink} variant="contained">
        Respond Now
      </Button>
    );
  }

  // Campaign is closed and results have been released
  if (response.campaignReleased && !submittable) {
    return (
      <Button href={response.campaignResultsLink} variant="text">
        View Results
      </Button>
    );
  }
  return null;
}

const SCHEDULED_STATES = ["SC", "ACS"];

export const ResponseCard = ({ response }: Props) => {
  const staleStates = ["CL", "AR"];
  const isScheduled = SCHEDULED_STATES.includes(response.campaignState);
  const submittable = !isScheduled && !(
    response.submitted || staleStates.includes(response.campaignState)
  );
  const viewable = Boolean(response.campaignReleased);
  const needsResponse =
    viewable &&
    !submittable &&
    !response.hasLoopResponse &&
    response.campaignLoopStudent &&
    response.campaignState === "CL";

  // Scheduled cards are clickable (navigates to "not yet opened" message) but not submittable
  const disabled = !isScheduled && !submittable && !viewable;
  const link = submittable || isScheduled
    ? response.tokenizedLink
    : response.campaignResultsLink;

  return (
    <Card sx={{ opacity: disabled ? 0.5 : 1.0 }}>
      <CardActionArea href={link} disabled={disabled}>
        <CardHeader
          action={<ResponseState response={response} />}
          title={response.campaignName}
          titleTypographyProps={{
            style: { color: "inherit", textDecoration: "none" },
          }}
          subheader={
            response.campaignDueDate
              ? `Due ${new Date(response.campaignDueDate).toLocaleDateString()}`
              : `No due date`
          }
        />
        <CardContent sx={{ display: "flex", justifyContent: "right", gap: 1 }}>
          {disabled && <ResponseProgress response={response} />}
          <UserAction
            response={response}
            needsResponse={needsResponse}
            submittable={submittable}
          />
        </CardContent>
      </CardActionArea>
    </Card>
  );
};
